import {
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Visibility,
  VisibilityOff,
  Work as WorkIcon,
} from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Modal,
  Paper,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ptBR } from "date-fns/locale";
import { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import authService from "../services/auth";
import { ESTADOS_BRASIL } from "../utils/constants";
import {
  formatCEP,
  formatCpfCnpj,
  formatNumero,
  formatPhone,
  formatUsername,
  validateCNPJ,
} from "../utils/formatters";
import { toISODate } from "../utils/utils";
import CharCounter from "../components/CharCounter";
import GerarSenhaButton from "../components/GerarSenhaButton";
import TermsOfUseModal from "../components/TermsOfUseModal";
import {
  splitNomeCompleto,
  TIPO_USUARIO_CONSULTOR,
} from "../utils/obterNomeExibicaoUsuario";

const TIPO_USUARIO_INTERESSADO = 0;

const CHAR_LIMITS = {
  username: 20,
  email: 50,
  senha: 50,
  ConfirmarSenha: 50,
  nome: 150,
  sobrenome: 150,
  cpf: 18,
  cnpj: 18,
  telefone: 15,
  rua: 100,
  numero: 10,
  cidade: 50,
  cep: 9,
  profissao: 50,
  empresa: 50,
  cargo: 50,
  razaoSocial: 150,
  nomeFantasia: 150,
  representanteLegal: 150,
};

function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorsDeValidacao, setErrorsDeValidacao] = useState([]);
  const [tipoUsuario, setTipoUsuario] = useState("interessado");
  const [successMessage, setSuccessMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [semNumero, setSemNumero] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    username: "",
    senha: "",
    ConfirmarSenha: "",
    email: "",
    nome: "",
    sobrenome: "",
    cpf: "",
    telefone: "",
    data_nascimento: null,
    rua: "",
    numero: "",
    cidade: "",
    estado: "",
    cep: "",
    profissao: "",
    empresa: "",
    cargo: "",
    area_formacao: "",
    tipo_estudo: "",
    cnpj: "",
    razaoSocial: "",
    nomeFantasia: "",
    dataConstituicao: null,
    representanteLegal: "",
  });

  const buscarCep = async (cepFormatado) => {
    const digits = cepFormatado.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          rua: data.logradouro || prev.rua,
          cidade: data.localidade || prev.cidade,
          estado: data.uf || prev.estado,
        }));
      }
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (semNumero && name === "numero") {
      return;
    }
    switch (name) {
      case "cpf":
        formattedValue = formatCpfCnpj(value);
        break;
      case "cnpj":
        formattedValue = formatCpfCnpj(value);
        break;
      case "telefone":
        formattedValue = formatPhone(value);
        break;
      case "cep":
        formattedValue = formatCEP(value);
        buscarCep(formattedValue);
        break;
      case "username":
        formattedValue = formatUsername(value);
        break;
      case "numero":
        formattedValue = formatNumero(value);
        break;
      default:
        formattedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleSemNumeroChange = (e) => {
    const checked = e.target.checked;
    setSemNumero(checked);

    setFormData((prev) => ({
      ...prev,
      numero: checked ? null : "",
    }));
  };

  const handleTipoUsuarioChange = (event, novoTipo) => {
    if (novoTipo !== null) {
      setTipoUsuario(novoTipo);
    }
  };



  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      setErrorsDeValidacao([]);

      if (!executeRecaptcha) {
        setErrorsDeValidacao([
          "reCAPTCHA não está pronto. Aguarde um momento.",
        ]);
        setOpenErrorModal(true);
        setLoading(false);
        return;
      }

      const recaptchaToken = await executeRecaptcha("register");

      if (!recaptchaToken) {
        setErrorsDeValidacao([
          "Falha ao obter token reCAPTCHA. Tente novamente.",
        ]);
        setOpenErrorModal(true);
        setLoading(false);
        return;
      }

      if (tipoUsuario !== "pessoa_juridica" && toISODate(formData.data_nascimento) === null) {
        setErrorsDeValidacao(["Data de nascimento inválida."]);
        setOpenErrorModal(true);
        setLoading(false);
        return;
      }

      if (tipoUsuario === "pessoa_juridica") {
        const cnpjDigits = (formData.cnpj || "").replace(/\D/g, "");
        if (cnpjDigits.length !== 14 || !validateCNPJ(cnpjDigits)) {
          setErrorsDeValidacao(["CNPJ inválido."]);
          setOpenErrorModal(true);
          setLoading(false);
          return;
        }
        if (toISODate(formData.dataConstituicao) === null) {
          setErrorsDeValidacao(["Data de constituição inválida."]);
          setOpenErrorModal(true);
          setLoading(false);
          return;
        }
      }

      if (!acceptedTerms) {
        setErrorsDeValidacao([
          "Para registrar, voce deve ler e aceitar os Termos de Uso da Plataforma.",
        ]);
        setOpenErrorModal(true);
        setLoading(false);
        return;
      }

      if (!semNumero && (!formData.numero || formData.numero.trim() === "")) {
        setErrorsDeValidacao(["O número do endereço é obrigatório."]);
        setOpenErrorModal(true);
        setLoading(false);
        return;
      }

      try {
        let payload;
        if (tipoUsuario === "pessoa_juridica") {
          const cnpjDigits = (formData.cnpj || "").replace(/\D/g, "");
          const { nome, sobrenome } = splitNomeCompleto(
            formData.representanteLegal,
            CHAR_LIMITS.nome,
          );
          payload = {
            username: formData.username,
            senha: formData.senha,
            ConfirmarSenha: formData.ConfirmarSenha,
            email: formData.email,
            nome,
            sobrenome,
            cpf: cnpjDigits,
            telefone: formData.telefone,
            data_nascimento: "1900-01-01",
            tipoUsuario: 4,
            razaoSocial: formData.razaoSocial,
            nomeFantasia: formData.nomeFantasia,
            dataConstituicao: toISODate(formData.dataConstituicao),
            representanteLegal: formData.representanteLegal,
            rua: formData.rua,
            numero: semNumero ? null : formData.numero,
            cidade: formData.cidade,
            estado: formData.estado,
            cep: formData.cep,
          };
        } else {
          payload = {
            ...formData,
            data_nascimento: toISODate(formData.data_nascimento),
            tipoUsuario:
              tipoUsuario === "consultor"
                ? TIPO_USUARIO_CONSULTOR
                : TIPO_USUARIO_INTERESSADO,
          };
        }

        await authService.register(payload, recaptchaToken);

        setSuccessMessage(
          "Cadastro realizado! Confirme seu email para ativar a conta. Redirecionando para o login...",
        );
        setOpenSnackbar(true);

        setTimeout(() => {
          navigate("/login", {
            state: {
              message:
                "Cadastro realizado com sucesso! Confira seu email e confirme sua conta para poder utilizar o sistema.",
            },
          });
        }, 5000);
      } catch (error) {
        const validation = error.errors;

        if (validation) {
          const mensagens = Object.values(validation).flat();
          setErrorsDeValidacao(mensagens);
        } else if (error.message) {
          const msg = [error.message];
          setErrorsDeValidacao(msg);
        } else {
          setErrorsDeValidacao(["Erro desconhecido."]);
        }

        setOpenErrorModal(true);
      } finally {
        setLoading(false);
      }
    },
    [acceptedTerms, executeRecaptcha, formData, semNumero, navigate, tipoUsuario],
  );

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3, md: 4 }, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <PersonAddIcon
                sx={{ fontSize: { xs: 32, md: 40 }, color: "primary.main", mr: 2 }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }}
              >
                Registro
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Selecione o tipo de cadastro:
            </Typography>
            <ToggleButtonGroup
              value={tipoUsuario}
              exclusive
              onChange={handleTipoUsuarioChange}
              aria-label="tipo de usuário"
              color="primary"
              fullWidth
              sx={{
                "& .MuiToggleButton-root": {
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                  py: { xs: 1, sm: 1.5 },
                  minHeight: { xs: 48, sm: "auto" },
                },
              }}
            >
              <ToggleButton value="interessado" aria-label="interessado">
                <PersonAddIcon sx={{ mr: 1 }} />
                Interessado
              </ToggleButton>
              <ToggleButton value="consultor" aria-label="consultor">
                <WorkIcon sx={{ mr: 1 }} />
                Consultor
              </ToggleButton>
              <ToggleButton value="pessoa_juridica" aria-label="pessoa jurídica">
                <BusinessIcon sx={{ mr: 1 }} />
                Pessoa Jurídica
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <form onSubmit={handleSubmit} aria-label="Formulário de registro">
            <Typography variant="h6" sx={{ mb: 2 }}>
              Dados de Acesso
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Login"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.username }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.username}
                          maxLength={CHAR_LIMITS.username}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.email }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.email}
                          maxLength={CHAR_LIMITS.email}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Senha"
                  name="senha"
                  type={showPassword ? "text" : "password"}
                  value={formData.senha}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.senha }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.senha}
                          maxLength={CHAR_LIMITS.senha}
                        />
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Confirmar Senha"
                  name="ConfirmarSenha"
                  type={showConfirm ? "text" : "password"}
                  value={formData.ConfirmarSenha}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.ConfirmarSenha }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.ConfirmarSenha}
                          maxLength={CHAR_LIMITS.ConfirmarSenha}
                        />
                        <IconButton
                          onClick={() => setShowConfirm(!showConfirm)}
                          edge="end"
                          aria-label={
                            showConfirm
                              ? "Ocultar confirmação da senha"
                              : "Mostrar confirmação da senha"
                          }
                        >
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flex: 1, order: { xs: 2, sm: 1 } }}
                  >
                    Para sua seguranca, evite utilizar uma senha que voce normalmente usa. Crie uma senha nova e exclusiva para este sistema.
                  </Typography>
                  <Box sx={{ order: { xs: 1, sm: 2 } }}>
                    <GerarSenhaButton
                      onGenerate={(senha) =>
                        setFormData((prev) => ({
                          ...prev,
                          senha,
                          ConfirmarSenha: senha,
                        }))
                      }
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {tipoUsuario !== "pessoa_juridica" ? (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Dados Pessoais
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.nome }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.nome}
                              maxLength={CHAR_LIMITS.nome}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Sobrenome"
                      name="sobrenome"
                      value={formData.sobrenome}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.sobrenome }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.sobrenome}
                              maxLength={CHAR_LIMITS.sobrenome}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="CPF/CNPJ"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.cpf }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.telefone }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={ptBR}
                    >
                      <DatePicker
                        label="Data de Nascimento"
                        value={formData.data_nascimento || null}
                        onChange={(newValue) =>
                          setFormData({
                            ...formData,
                            data_nascimento: newValue,
                          })
                        }
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            name: "data_nascimento",
                          },
                        }}
                        disableOpenPicker={false}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Dados da Empresa
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Razão Social"
                      name="razaoSocial"
                      value={formData.razaoSocial}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.razaoSocial }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.razaoSocial}
                              maxLength={CHAR_LIMITS.razaoSocial}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Nome Fantasia"
                      name="nomeFantasia"
                      value={formData.nomeFantasia}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.nomeFantasia }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.nomeFantasia}
                              maxLength={CHAR_LIMITS.nomeFantasia}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="CNPJ"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.cnpj }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.telefone }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={ptBR}
                    >
                      <DatePicker
                        label="Data de Constituição"
                        value={formData.dataConstituicao || null}
                        onChange={(newValue) =>
                          setFormData({
                            ...formData,
                            dataConstituicao: newValue,
                          })
                        }
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            name: "dataConstituicao",
                          },
                        }}
                        disableOpenPicker={false}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Representante Legal"
                      name="representanteLegal"
                      value={formData.representanteLegal}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.representanteLegal }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.representanteLegal}
                              maxLength={CHAR_LIMITS.representanteLegal}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <Divider sx={{ my: 4 }} />

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Endereço
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={semNumero}
                    onChange={handleSemNumeroChange}
                    sx={{
                      color: "#205723",
                      "&.Mui-checked": { color: "#205723" },
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                      borderRadius: "6px",
                    }}
                  />
                }
                label="Sem número"
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "15px",
                    color: "#333",
                  },
                }}
              />
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={9}>
                <TextField
                  required
                  fullWidth
                  label="Rua"
                  name="rua"
                  value={formData.rua}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.rua }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.rua}
                          maxLength={CHAR_LIMITS.rua}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="Número"
                  name="numero"
                  value={formData.numero ?? ""}
                  onChange={handleChange}
                  disabled={semNumero}
                  inputProps={{ maxLength: CHAR_LIMITS.numero }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.numero}
                          maxLength={CHAR_LIMITS.numero}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Cidade"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.cidade }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CharCounter
                          value={formData.cidade}
                          maxLength={CHAR_LIMITS.cidade}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required>
                  <InputLabel id="estado-label">Estado</InputLabel>
                  <Select
                    labelId="estado-label"
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                    label="Estado"
                  >
                    <MenuItem value="">
                      <em>Selecione</em>
                    </MenuItem>
                    {ESTADOS_BRASIL.map((estado) => (
                      <MenuItem key={estado.sigla} value={estado.sigla}>
                        {estado.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  required
                  fullWidth
                  label="CEP"
                  name="cep"
                  value={formData.cep}
                  onChange={handleChange}
                  inputProps={{ maxLength: CHAR_LIMITS.cep }}
                  InputProps={{
                    endAdornment: cepLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>
            </Grid>

            {tipoUsuario !== "pessoa_juridica" && (
              <>
                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ mb: 2 }}>
                  Informações Profissionais
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Profissão"
                      name="profissao"
                      value={formData.profissao}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.profissao }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.profissao}
                              maxLength={CHAR_LIMITS.profissao}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.empresa }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.empresa}
                              maxLength={CHAR_LIMITS.empresa}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cargo"
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleChange}
                      inputProps={{ maxLength: CHAR_LIMITS.cargo }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CharCounter
                              value={formData.cargo}
                              maxLength={CHAR_LIMITS.cargo}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <Box
              sx={{
                mt: 4,
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  size="medium"
                  fullWidth
                  sx={{
                    py: 0.75,
                    px: 2.25,
                    flex: { xs: "none", sm: 1 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                  onClick={() => setOpenTermsModal(true)}
                >
                  Ler Termos de Uso
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  size="medium"
                  fullWidth
                  sx={{
                    py: 0.75,
                    px: 2.5,
                    flex: { xs: "none", sm: 1 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                  disabled={loading || !acceptedTerms}
                >
                  {loading ? "Processando..." : "Registrar"}
                </Button>
              </Box>

              <Typography
                variant="caption"
                color={acceptedTerms ? "success.main" : "text.secondary"}
                sx={{
                  width: "100%",
                  textAlign: "right",
                  fontSize: "0.75rem",
                  mt: 0.25,
                  pr: { xs: 0, md: 0.5 },
                }}
              >
                {acceptedTerms
                  ? "Termos de uso lidos e aceitos"
                  : "Leia os Termos de Uso para habilitar o registro"}
              </Typography>
            </Box>

            <Box sx={{ mt: 2.5, textAlign: "center" }}>
              <Typography variant="body2">
                Já possui uma conta?{" "}
                <Link component={RouterLink} to="/login">
                  Faça login
                </Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ maxWidth: { xs: "90vw", sm: 560 }, left: "50%", transform: "translateX(-50%) !important", right: "auto" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Modal
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={openErrorModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 500, md: 800 },
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Erro ao registrar usuário:
            </Typography>

            <ul>
              {errorsDeValidacao.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>

            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => setOpenErrorModal(false)}
            >
              Fechar
            </Button>
          </Box>
        </Fade>
      </Modal>

      <TermsOfUseModal
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        onConfirm={() => {
          setAcceptedTerms(true);
          setOpenTermsModal(false);
        }}
      />
    </Container>
  );
}

export default Register;
